import { useEffect, useState } from "react";
import { toast } from "sonner";
import apiClient from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings2, Save, AlertTriangle } from "lucide-react";

const FEE_KEYS = [
  ["phe_main", "Post-Higher Ed — Main"], ["phe_adult", "Post-Higher Ed — Adult 18+"], ["phe_child", "Post-Higher Ed — Under 18"],
  ["pv_main", "Post-Vocational — Main"], ["pv_adult", "Post-Vocational — Adult 18+"], ["pv_child", "Post-Vocational — Under 18"],
  ["second_main", "Second stream — Main"], ["second_adult", "Second stream — Adult 18+"], ["second_child", "Second stream — Under 18"],
];
const DUR_KEYS = [["phe_bachelor", "PHE — Bachelor"], ["phe_master", "PHE — Master"], ["phe_phd", "PHE — PhD"], ["pv", "Post-Vocational"], ["second", "Second stream"], ["hk_bno", "HK / BNO holders"]];
const META_KEYS = [["source_url", "Source URL"], ["source_authority", "Source authority"], ["points_source_date", "Points table source date"], ["last_verified", "Last verified (YYYY-MM-DD)"], ["last_reviewed", "Last reviewed (YYYY-MM-DD)"], ["reviewed_by", "Reviewed by"], ["next_review", "Next review (YYYY-MM-DD)"]];

export default function AdminAssessmentRules() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/admin/assessment-rules").then((r) => setRules(r.data)).catch(() => toast.error("Failed to load rules"));
  }, []);

  if (!rules) return <div className="p-8 text-slate-500">Loading…</div>;

  const set = (k, v) => setRules((r) => ({ ...r, [k]: v }));
  const setNested = (group, k, v) => setRules((r) => ({ ...r, [group]: { ...r[group], [k]: v } }));
  const setTest = (i, k, v) => setRules((r) => {
    const tests = [...r.english_tests];
    tests[i] = { ...tests[i], [k]: k === "test" ? v : Number(v) || 0 };
    return { ...r, english_tests: tests };
  });

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/admin/assessment-rules", rules);
      toast.success("Assessment rules saved — reports now use the updated values.");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="admin-assessment-rules">
      <h1 className="font-display text-3xl text-[#29324A] flex items-center gap-3">
        <Settings2 className="h-7 w-7 text-[#29324A]" /> 485 Assessment Rules
      </h1>
      <p className="text-sm text-slate-500 mt-1">Fees, age limits, English requirements and review dates used by the 485 Pathway Planner. Reports read these values live.</p>

      {rules.needs_review && (
        <div className="mt-4 bg-amber-50 border-l-4 border-amber-500 p-4 flex items-start gap-3" data-testid="rules-review-flag">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-[13px] text-amber-900">These rules were last reviewed more than {rules.review_interval_days || 30} days ago. Visa settings, occupation lists, fees and state criteria can change — please verify against the source and update the &quot;Last reviewed&quot; date.</p>
        </div>
      )}

      <Section title="Application charges (AUD)">
        <div className="grid sm:grid-cols-3 gap-4">
          {FEE_KEYS.map(([k, label]) => (
            <F key={k} label={label}><Input type="number" data-testid={`rule-fee-${k}`} value={rules.fees?.[k] ?? ""} onChange={(e) => setNested("fees", k, Number(e.target.value))} /></F>
          ))}
        </div>
      </Section>

      <Section title="Age limits">
        <div className="grid sm:grid-cols-3 gap-4">
          <F label="Standard age limit"><Input type="number" data-testid="rule-age-limit" value={rules.age_limit ?? ""} onChange={(e) => set("age_limit", Number(e.target.value))} /></F>
          <F label="Exception age limit (research / HK-BNO)"><Input type="number" value={rules.age_exception_limit ?? ""} onChange={(e) => set("age_exception_limit", Number(e.target.value))} /></F>
          <F label="Completion window (months)"><Input type="number" value={rules.completion_window_months ?? ""} onChange={(e) => set("completion_window_months", Number(e.target.value))} /></F>
        </div>
      </Section>

      <Section title="Accepted English tests & minimum scores">
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-2 text-[10px] font-bold tracking-wider uppercase text-slate-500">
            <span>Test</span><span>Overall min</span><span>Component min</span><span>Validity (months)</span>
          </div>
          {(rules.english_tests || []).map((t, i) => (
            <div key={i} className="grid grid-cols-4 gap-2">
              <Input value={t.test} onChange={(e) => setTest(i, "test", e.target.value)} />
              <Input type="number" step="0.5" value={t.overall} onChange={(e) => setTest(i, "overall", e.target.value)} />
              <Input type="number" step="0.5" value={t.component} onChange={(e) => setTest(i, "component", e.target.value)} />
              <Input type="number" value={t.validity_months} onChange={(e) => setTest(i, "validity_months", e.target.value)} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Stream durations">
        <div className="grid sm:grid-cols-3 gap-4">
          {DUR_KEYS.map(([k, label]) => (
            <F key={k} label={label}><Input value={rules.durations?.[k] ?? ""} onChange={(e) => setNested("durations", k, e.target.value)} /></F>
          ))}
        </div>
      </Section>

      <Section title="Time-risk thresholds (months remaining)">
        <div className="grid sm:grid-cols-3 gap-4">
          <F label="Low risk above"><Input type="number" value={rules.time_risk?.low_months ?? ""} onChange={(e) => setNested("time_risk", "low_months", Number(e.target.value))} /></F>
          <F label="Moderate risk above"><Input type="number" value={rules.time_risk?.moderate_months ?? ""} onChange={(e) => setNested("time_risk", "moderate_months", Number(e.target.value))} /></F>
          <F label="High risk above"><Input type="number" value={rules.time_risk?.high_months ?? ""} onChange={(e) => setNested("time_risk", "high_months", Number(e.target.value))} /></F>
        </div>
      </Section>

      <Section title="Source & legal review">
        <div className="grid sm:grid-cols-2 gap-4">
          {META_KEYS.map(([k, label]) => (
            <F key={k} label={label}><Input data-testid={`rule-${k}`} value={rules[k] ?? ""} onChange={(e) => set(k, e.target.value)} /></F>
          ))}
          <F label="Review interval (days)"><Input type="number" value={rules.review_interval_days ?? 30} onChange={(e) => set("review_interval_days", Number(e.target.value))} /></F>
        </div>
      </Section>

      <button onClick={save} disabled={saving} data-testid="rules-save-btn"
        className="mt-8 inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] disabled:opacity-60 text-white text-xs font-bold tracking-[0.2em] uppercase px-7 py-4">
        <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Rules"}
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8 bg-white border border-slate-200 p-6">
      <h2 className="font-display text-lg text-[#29324A] mb-4">{title}</h2>
      {children}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold text-[#29324A] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
